import type { Page } from './feature';
import { q, qa } from '../shared/dom';
import { bus } from '../shared/events';
import { applyLang, t } from '../shared/i18n';
import { loadPersisted, persist, setState } from '../shared/store/store';
import { blankState, migrate } from '../shared/store/seed';
import { hydrateFromApi } from '../shared/store/sync';
import { currentUser, restoreSession } from '../shared/session/session';
import { toast, toastTemplate } from '../shared/ui/toast';
import { installMoneyInputs } from '../shared/ui/money-input';
import { installResponsiveTables } from '../shared/ui/responsive-tables';
import { DEFAULT_PATH, currentPath, currentQuery, matchPage, navigate, pagesFor, type RouteMatch } from './router';
import { createSidebar } from './layout/sidebar';
import { topbar } from './layout/topbar';
import { authShell } from '../features/auth/auth-shell';
import { clearAuthHash, isAuthHash } from '../features/auth/auth-routes';
import { hostShell } from '../features/host/host-shell';
import { overview } from '../features/overview/overview';
import { events } from '../features/events/events';
import { eventDetail } from '../features/events/event-detail';
import { eventNew } from '../features/events/event-new';
import { missionsView } from '../features/missions/missions';
import { missionsList } from '../features/missions/missions-list';
import { players } from '../features/players/players';
import { playerDetail } from '../features/players/player-detail';
import { tickets } from '../features/tickets/tickets';
import { hosts } from '../features/hosts/hosts';
import { hostDetail } from '../features/hosts/host-detail';
import { usersView } from '../features/users/users';
import { settings } from '../features/settings/settings';
import { closeStage, handleKey, isOpen, studio } from '../features/studio/studio';
import { sizeCanvases } from '../features/studio/fx/canvas';

/** Páginas do backoffice. A ordem define o menu. */
const pages: Page[] = [overview, events, eventNew, eventDetail, hosts, hostDetail, players, playerDetail, tickets, missionsView, missionsList, usersView, settings];

/**
 * Monta a aplicação: estado local do estúdio, sessão na API, casca (login / anfitrião /
 * backoffice com menu lateral e rotas) e o estúdio ao vivo.
 */
export async function bootstrap(root: HTMLElement): Promise<void> {
  const sidebar = createSidebar(pages);

  root.innerHTML = [
    authShell.template(),
    hostShell.template(),
    `<div id="backoffice" class="shell" hidden>
       ${sidebar.template()}
       <div class="main-col">
         ${topbar.template()}
         <main id="app-main">${pages.map(p => `<section class="page" id="page-${p.id}" hidden>${p.template()}</section>`).join('')}</main>
       </div>
     </div>`,
    studio.template(),
    toastTemplate()
  ].join('');

  /* estado do estúdio: cache persistido (tudo o mais vem da API ao entrar) */
  const saved = loadPersisted();
  setState(migrate(saved ?? blankState()));
  persist();

  /* componentes */
  installMoneyInputs();
  installResponsiveTables();
  authShell.mount();
  hostShell.mount();
  sidebar.mount();
  topbar.mount();
  pages.forEach(p => p.mount());
  studio.mount();

  let current: RouteMatch | null = null;

  /** Decide o que aparece a partir do hash e do papel. */
  const route = (): void => {
    const u = currentUser();
    if (!u) { authShell.render(); return; }
    if (u.role === 'HOST') { hostShell.render(); return; }
    if (isAuthHash() || currentPath() === '/') { navigate(DEFAULT_PATH, true); return; }
    const match = matchPage(pagesFor(pages, u.role), currentPath());
    if (!match) { navigate(DEFAULT_PATH, true); return; }
    current = match;
    qa('.page').forEach(p => { p.hidden = p.id !== 'page-' + match.page.id; });
    sidebar.setActive(match.page.id);
    topbar.setTitle(t((match.page.nav ?? pages.find(p => p.id === match.page.navParent)?.nav)?.key ?? ''));
    match.page.render(match.params, currentQuery());
    window.scrollTo({ top: 0 });
  };

  /** Redesenha a página atual (estado ou idioma mudaram). */
  const rerender = (): void => {
    const u = currentUser();
    if (!u) { authShell.render(); return; }
    if (u.role === 'HOST') { hostShell.render(); return; }
    sidebar.render(); topbar.render();
    if (current) { current.page.render(current.params, currentQuery()); sidebar.setActive(current.page.id); }
  };

  /** Quem entrou decide a casca: admin/suporte → backoffice, influenciador → painel dele. */
  const applySession = (): void => {
    const u = currentUser();
    const backoffice = u?.role === 'ADMIN' || u?.role === 'SUPPORT';
    if (!u && isOpen()) closeStage();
    if (!u) topbar.setTitle('');
    if (u) clearAuthHash();
    q('#auth').hidden       = !!u;
    q('#backoffice').hidden = !backoffice;
    q('#host-shell').hidden = u?.role !== 'HOST';
    if (backoffice) { sidebar.render(); topbar.render(); }
    route();
    if (u) void hydrateFromApi().catch(() => toast(t('msg.syncFailed')));
  };

  bus.on('state', rerender);
  bus.on('lang', rerender);
  bus.on('session', applySession);
  window.addEventListener('hashchange', () => { if (currentUser()) route(); });

  await restoreSession();     // token salvo → /auth/me
  applyLang();
  applySession();

  window.addEventListener('resize', sizeCanvases);
  document.addEventListener('keydown', handleKey);
}
